import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const docMeta = [
  { file: "gocems-brochure.pdf", type: "PDF", size: "2.1 MB", icon: "menu_book", badge: "Ghana" },
  { file: "kocems-presentation.pdf", type: "PDF", size: "4.6 MB", icon: "slideshow", badge: "Kenya" },
  { file: "tocems-presentation.pdf", type: "PDF", size: "14.8 MB", icon: "slideshow", badge: "Tanzania" },
  { file: "nimacems-presentation.pdf", type: "PDF", size: "5.2 MB", icon: "slideshow", badge: "Nigeria" },
  { file: "aocems-methodology-whitepaper.txt", type: "TXT", size: "1.5 KB", icon: "description" },
  { file: "aocems-sensor-calibration-manual.txt", type: "TXT", size: "1.6 KB", icon: "engineering" },
  { file: "aocems-openapi.json", type: "JSON", size: "1.4 KB", icon: "code" },
  { file: "aocems-esg-schema.txt", type: "TXT", size: "0.7 KB", icon: "schema" },
  { file: "aocems-risk-mitigation-playbook.txt", type: "TXT", size: "1.2 KB", icon: "menu_book" },
  { file: "aocems-operator-onboarding-kit.txt", type: "TXT", size: "1.3 KB", icon: "inventory_2" },
];

function HeroResources() {
  const { t } = useTranslation();
  return (
    <section className="relative pt-32 pb-xl overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-cream to-background" />
      <div className="absolute inset-0 tech-grid opacity-30" />

      <div className="absolute inset-0 z-0 pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.6, 0.4], y: [0, -30, 0], rotate: [0, i % 2 === 0 ? 6 : -6, 0] }}
            transition={{ duration: 6 + i * 0.5, repeat: Infinity, delay: i * 0.3, ease: "easeInOut" }}
            style={{ left: `${(i * 13 + 10) % 90}%`, top: `${(i * 17 + 20) % 70}%` }}
            className="absolute w-16 h-20 bg-white rounded-md shadow-lg border border-outline-variant flex flex-col items-center justify-end p-1.5"
          >
            <span className="material-symbols-outlined text-brand text-[16px]">description</span>
            <div className="w-full h-1 bg-zinc-200 rounded mt-1" />
            <div className="w-3/4 h-1 bg-zinc-200 rounded mt-1" />
            <div className="w-1/2 h-1 bg-brand/40 rounded mt-1" />
          </motion.div>
        ))}
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10 text-center">
        <motion.span
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="font-label-caps text-on-tertiary-container bg-tertiary-fixed px-3 py-1 rounded-full mb-6 inline-block"
        >
          {t("resources.hero.eyebrow")}
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="font-h1 text-[40px] md:text-h1 text-primary mb-6 max-w-3xl mx-auto"
        >
          {t("resources.hero.title_a")} <span className="text-gradient">{t("resources.hero.title_accent")}</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.25 }}
          className="font-body-lg text-on-surface-variant max-w-2xl mx-auto"
        >
          {t("resources.hero.intro")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-10 max-w-xl mx-auto flex"
        >
          <input
            data-testid="resources-search"
            type="search"
            placeholder={t("resources.hero.search_placeholder")}
            className="flex-1 px-5 py-4 rounded-l-xl border border-r-0 border-outline-variant bg-white text-sm focus:outline-none focus:border-brand"
          />
          <button className="px-6 bg-brand text-white rounded-r-xl font-bold hover:opacity-90 active:scale-95 transition-all">
            <span className="material-symbols-outlined">search</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function Library() {
  const { t } = useTranslation();
  return (
    <section className="max-w-[1280px] mx-auto px-6 py-xl">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-gutter">
        {docMeta.map((d, i) => (
          <motion.a
            key={d.file}
            href={`/docs/${d.file}`}
            download={d.file}
            data-testid={`resource-download-${d.file}`}
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: i * 0.06 }}
            className="bg-white p-md rounded-2xl border border-outline-variant hover:border-brand hover:shadow-lg transition-all group cursor-pointer block"
          >
            <div className="flex items-start justify-between mb-md">
              <div className="w-12 h-12 rounded-xl bg-tertiary-fixed flex items-center justify-center">
                <span className="material-symbols-outlined text-brand">{d.icon}</span>
              </div>
              <div className="flex items-center gap-2">
                {d.badge && (
                  <span className="text-[10px] uppercase tracking-widest text-brand font-bold bg-brand/10 px-2 py-0.5 rounded-full">
                    {d.badge}
                  </span>
                )}
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{d.type}</span>
              </div>
            </div>
            <h4 className="font-h3 text-h3 text-primary mb-2">{t(`resources.docs.${d.file}`)}</h4>
            <div className="flex items-center justify-between mt-md">
              <span className="text-xs text-on-surface-variant">{d.size}</span>
              <span className="material-symbols-outlined text-brand group-hover:translate-x-1 transition-transform">download</span>
            </div>
          </motion.a>
        ))}
      </div>
    </section>
  );
}

function Faq() {
  const { t } = useTranslation();
  const faqs = t("resources.faq.items", { returnObjects: true }) || [];

  return (
    <section className="bg-surface-container-low py-xl">
      <div className="max-w-[920px] mx-auto px-6">
        <div className="mb-lg text-center">
          <h2 className="font-h2 text-h2 text-primary">{t("resources.faq.title")}</h2>
          <div className="w-20 h-1 bg-brand mx-auto mt-sm" />
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <motion.details
              key={i}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.4, delay: i * 0.06 }}
              className="bg-white rounded-2xl border border-outline-variant overflow-hidden group"
            >
              <summary className="cursor-pointer list-none px-md py-4 flex items-center justify-between font-manrope font-bold text-primary">
                {f.q}
                <span className="material-symbols-outlined text-brand transition-transform group-open:rotate-180">expand_more</span>
              </summary>
              <div className="px-md pb-md text-on-surface-variant text-sm">{f.a}</div>
            </motion.details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Resources() {
  return (
    <>
      <HeroResources />
      <Library />
      <Faq />
    </>
  );
}
